import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { policiesKeys } from "../queryKeys/queryKeys";
import { MenuMultiOption } from "@saviynt/design-system";
import { toSnakeCase } from "@repo/mfe-core";
import { PolicyPayload } from "@repo/api/schemas/policies.schema";
import { ActionType } from "../pages/PoliciesForm/PoliciesForm";
import { policiesApi, QueryPayload } from "@repo/api/clients/policies.api";
import { useIntl } from "react-intl";
import { TimelineEventResponse } from "@repo/api/schemas/policiesInventory.schema";
import {
  AssetTimelineRequestPayload,
  policiesInventoryApi,
} from "@repo/api/clients/policiesInventory.api";

interface UsePoliciesListProps {
  searchValue: string;
  selectedFilters: Record<string, MenuMultiOption[]>;
  sort?: string;
  order?: string;
}

export const usePoliciesList = (
  filterValues: UsePoliciesListProps,
  options?: { enabled?: boolean },
) => {
  return useInfiniteQuery({
    queryKey: policiesKeys.policyList(filterValues),
    queryFn: async ({ pageParam }: { pageParam: number }) => {
      try {
        const { searchValue, selectedFilters } = filterValues;

        const filters = Object.fromEntries(
          Object.entries(selectedFilters)
            .filter(([, options]) => options?.length)
            .map(([key, options]) => [
              toSnakeCase(key),
              options.map(({ value }) =>
                value === "true" ? true : value === "false" ? false : value,
              ),
            ]),
        );

        return await policiesApi.getPoliciesList({
          searchValue,
          ...filters,
          sort: filterValues.sort,
          order: filterValues.order as "asc" | "desc" | undefined,
          offset: pageParam,
          limit: 20,
        });
      } catch (error) {
        console.error("Failed to fetch Policy List:", error);
        throw error;
      }
    },
    getNextPageParam: (lastPage) => {
      return lastPage.nextToken ?? undefined;
    },
    initialPageParam: 0,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
    enabled: options?.enabled ?? true,
  });
};

export function useGetPolicyById(policyId: string) {
  return useQuery({
    queryKey: policiesKeys.policyById(policyId),
    queryFn: () => policiesApi.getPolicyById(policyId),
    enabled: !!policyId,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
}

export function useGetFiltersList() {
  return useQuery({
    queryKey: policiesKeys.policiesFilters(),
    queryFn: async () => {
      return await policiesApi.getPolicyFilters();
    },
    retry: 1,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export function useDeletePolicy({
  onError,
  onSuccess,
  onSettled,
}: {
  onError: (message: string) => void;
  onSuccess?: () => void;
  onSettled?: () => void;
}) {
  const intl = useIntl();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (policyId: string) => {
      return await policiesApi.deletePolicy(policyId);
    },
    onSuccess: () => {
      // Invalidate the policy query to refresh the list
      queryClient.invalidateQueries({
        queryKey: [...policiesKeys.all, "policyList"],
        exact: false,
      });
      onSuccess?.();
    },
    onError: () => {
      onError(
        intl.formatMessage({
          id: "a+ziIO",
          defaultMessage: "Failed to delete policy",
          description: "Error message for policy delete failure",
        }),
      );
    },
    onSettled: () => {
      onSettled?.();
    },
  });
}

export function useCreatePolicy({
  onError,
  onSuccess,
}: {
  onError: (message: string) => void;
  onSuccess?: () => void;
}) {
  const intl = useIntl();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ payload }: { payload: PolicyPayload }) =>
      policiesApi.createPolicy({
        payload,
      }),
    onError: () => {
      onError(
        intl.formatMessage({
          id: "on6hYe",
          defaultMessage: "Failed to Create policy",
          description: "Error message for policy Create failure",
        }),
      );
    },
    onSuccess: () => {
      // Invalidate the policy query to refresh the list
      queryClient.invalidateQueries({
        queryKey: [...policiesKeys.all, "policyList"],
        exact: false,
      });
      onSuccess?.();
    },
  });
}
export function useUpdateClonePolicy({
  onError,
  onSuccess,
}: {
  onError: (message: string) => void;
  onSuccess?: () => void;
}) {
  const intl = useIntl();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      payload,
      policyId,
      actionType,
    }: {
      payload: Partial<PolicyPayload>;
      policyId: string;
      actionType: ActionType;
    }) => {
      if (actionType === "edit") {
        return policiesApi.updatePolicy({
          payload,
          policyId,
        });
      } else {
        return policiesApi.clonePolicy({
          payload: payload as PolicyPayload,
          policyId,
        });
      }
    },
    onError: () => {
      onError(
        intl.formatMessage({
          id: "WLCuPL",
          defaultMessage: "Failed to Update policy",
          description: "Error message for policy update failure",
        }),
      );
    },
    onSuccess: () => {
      // Invalidate the policy query to refresh the list
      queryClient.invalidateQueries({
        queryKey: [...policiesKeys.all, "policyList"],
        exact: false,
      });
      onSuccess?.();
    },
  });
}

export function useExecuteQuery(payload?: QueryPayload) {
  return useInfiniteQuery({
    queryKey: policiesKeys.executeQuery(payload),
    queryFn: async ({ pageParam }: { pageParam: number }) => {
      try {
        return await policiesApi.queryPolicies({
          ...payload!,
          offset: pageParam,
        });
      } catch (error) {
        console.error("Failed to fetch Policy List:", error);
        throw error;
      }
    },
    getNextPageParam: (lastPage) => {
      if (!lastPage.has_more) return undefined;

      return lastPage.offset + lastPage.limit;
    },
    initialPageParam: 0,
    retry: 1,
    enabled: !!payload,
  });
}

export function useQuerySuggestions(paylaod: {
  text: string;
  cursor_pos: number;
}) {
  return useQuery({
    queryKey: policiesKeys.querySuggestion(paylaod.text),
    queryFn: async () => policiesApi.querySuggestions(paylaod),
    enabled: !!paylaod.text,
    retry: 1,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export const useInventoryAssetSummaryData = (assetId: string) => {
  return useQuery({
    queryKey: policiesKeys.inventorySummaryData(assetId),
    queryFn: () => policiesInventoryApi.inventorySummaryData(assetId),
    retry: false,
  });
};

export const useInventoryAssetFindigsData = (assetId: string) => {
  return useQuery({
    queryKey: policiesKeys.findingsTabData(assetId),
    queryFn: () => policiesInventoryApi.inventoryFindigsData(assetId),
    retry: false,
  });
};

export const useAssetTimelineData = ({
  assetId,
  requestPayload,
  enabled = true,
  pageSize,
}: {
  assetId: number;
  requestPayload: AssetTimelineRequestPayload;
  enabled?: boolean;
  pageSize?: number;
}) => {
  return useInfiniteQuery<TimelineEventResponse>({
    initialPageParam: null,
    queryKey: [
      ...policiesKeys.inventoryTimelineData(String(assetId)),
      requestPayload,
      pageSize,
    ],
    queryFn: ({ pageParam }) =>
      policiesInventoryApi.inventoryTimelineData({
        requestPayload,
        assetId,
        pageParam: pageParam as string,
        pageSize,
      }),
    getNextPageParam: (lastPage) => lastPage.nextPageToken || undefined,
    enabled,
  });
};

export function useGetPolicyTags() {
  return useQuery({
    queryKey: policiesKeys.policyTags(),
    queryFn: () => policiesApi.getPolicyTags(),
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
}
